import { plateFont } from '../../utils/fonts';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Box, Chip, Typography } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

/**
 * Shows a selected customer's existing vehicles (derived from their work orders)
 * as clickable chips, so the user can pick a vehicle already in the system
 * without remembering its registration number.
 *
 * Renders nothing when there is no customer or the customer has no vehicles.
 *
 * Props:
 * - customerId: id of the selected customer (falsy => render nothing)
 * - onSelect: (vehicle) => void, called with the chosen vehicle summary object
 * - sx: optional style overrides for the wrapper Box
 */
function CustomerVehicleChips({ customerId, onSelect, sx }) {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    if (!customerId) {
      setVehicles([]);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    axios
      .get(`/api/vehicles/by-customer/?customer_id=${encodeURIComponent(customerId)}`, {
        signal: controller.signal,
      })
      .then((response) => {
        if (!cancelled) {
          setVehicles(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVehicles([]);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [customerId]);

  if (!customerId || vehicles.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1.5, ...sx }}>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          color: 'text.secondary',
          mb: 0.75,
        }}
      >
        {t("Customer's vehicles")}
      </Typography>
      <Box data-testid="customer-vehicle-chips" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {vehicles.map((vehicle) => {
          const makeModel = [vehicle.manufacturer, vehicle.model].filter(Boolean).join(' ');
          const label = [
            vehicle.registration_number,
            makeModel,
            vehicle.year_model ? `(${vehicle.year_model})` : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <Chip
              key={vehicle.id}
              data-testid="customer-vehicle-chip"
              data-registration={vehicle.registration_number}
              icon={<DirectionsCarIcon />}
              label={label}
              variant="outlined"
              clickable
              onClick={() => onSelect(vehicle)}
              sx={{
                borderRadius: '8px',
                '& .MuiChip-icon': { fontSize: 16 },
                '& .MuiChip-label': { ...plateFont, fontSize: '12px' },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default CustomerVehicleChips;
